import { Link } from "react-router-dom";
import {
  PencilSquareIcon,
  BuildingOfficeIcon,
  EyeIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from '@heroicons/react/20/solid';

import StatusBadge from '../StatusBadge.jsx';
import Loading from '../Loading.jsx';

const IntegrationsLists = ({ integrations }) => {

  if (!integrations || integrations.length === 0) {
    return <Loading />;
  }
  const renderConnectionButtons = (connection) => {
    const editAccess = connection?.permissions?.includes('edit');
    const viewAccess = connection?.permissions?.includes('view') || editAccess;
    const connectionStatus = connection.connection_status;
    const unmappedProducts = connection.unmapped_products;
    const credentialStatus = connection.credential_status;
    const frontendType = connection.frontendType;
    if (connectionStatus === "active" || connectionStatus === "configuring") {
      return (
        <>
          {viewAccess && !connection.tabs.some(tab => tab.id === "upload") ? <div className="flex w-0 flex-1">
            <Link
              to={`/integrations/${connection.integration_id}`}
              state={{ tab: credentialStatus === 'invalid' && frontendType === 'pso' ? 'onboarding' : 'status' }}
              className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
            >
              {credentialStatus === 'invalid' && frontendType === 'pso' ? (
                <>
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                  Fix Setup
                </>
              ) : (
                <>
                  <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  Details
                </>
              )}
            </Link>
          </div>: null}
          {viewAccess && connection.tabs.some(tab => tab.id === "upload") ? <div className="flex w-0 flex-1">
            <Link
              to={`/integrations/${connection.integration_id}`}
              state={{ tab: 'upload' }}
              className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
            >
              <ArrowUpTrayIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              Upload
            </Link>
          </div>: null}
          {editAccess? <div className="flex w-0 flex-1">
            <Link
              to={`/integrations/${connection.integration_id}`}
              state={{ tab: 'products' }}
              className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
            >
              <PencilSquareIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              Products
              {unmappedProducts > 0 && (
                <div className="relative group">
                  <ExclamationTriangleIcon 
                    className="h-5 w-5 text-yellow-600 cursor-help" 
                    aria-hidden="true"
                  />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-0 pointer-events-none z-50">
                    {unmappedProducts} Unmapped Products
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              )}
            </Link>
          </div>: null}
        </>
      );
    } else if (editAccess && connection.available) {
      return (
        <div className="flex w-0 flex-1">
          <Link
            to={`/integrations/${connection.integration_id}`}
            className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
          >
            <PencilSquareIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            Set Up Integration
          </Link>
        </div>
      )
    } else {
      return (
        <div className="flex w-0 flex-1">
          <Link
            id="support"
            className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
          >
            <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            Contact Support
          </Link>
        </div>
      )
    }
  }

  const renderConnection = (connection) => {
    return (
      <li key={connection.name} className="col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow">
        <div className="flex w-full items-center justify-between p-6">
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h3 className="truncate text-sm font-medium text-gray-900">{connection.name}</h3>
            </div>
            <StatusBadge status={connection.connection_status} credentialStatus={connection.credential_status} unmappedProducts={connection?.unmapped_products ?? 0} />
            <p className="mt-1 text-sm text-gray-500">{connection.title}</p>
          </div>
          {connection.imageUrl ? (
            <img className="h-[6rem] w-[6rem] flex-shrink-0 rounded-full bg-white" src={connection.imageUrl} alt="" />
          ) : (
              <BuildingOfficeIcon className="h-[6rem] w-[6rem] text-gray-200" aria-hidden="true" />
            )}
        </div>
        <div>
          <div className="-mt-px flex divide-x divide-gray-200">
            {renderConnectionButtons(connection)}
          </div>
        </div>
      </li>
    )
  }

  return (
    <ul role="list" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {integrations.sort((a,b) => a.name.localeCompare(b.name)).map((connection) => (
        renderConnection(connection)
      ))}
    </ul>
  )
};

export default IntegrationsLists;
